<form #f="ngForm" (ngSubmit)="onSave(false)">
  <!-- Default roles are system-managed: their name and permissions are read-only. Only the
       associated employees can be changed (via the Employees tab). -->
  <div class="alert alert-info m-3" *ngIf="isDefault">
    <mat-icon class="align-middle mr-2">info</mat-icon>
    This is a default role. Its name and permissions are managed by the system and cannot be changed.
    Use the Employees tab to assign or remove employees.
  </div>

  <!-- Partner roles: read-only to clients, editable (but overwritten by the partner sync) for super admins. -->
  @if (isPartnerRole) {
    @if (isReadOnly) {
      <div class="alert alert-info m-3">
        <mat-icon class="align-middle mr-2">info</mat-icon>
        This role is managed by {{ utils.getAppName() }}. Its name, permissions and members can only be changed by {{ utils.getAppName() }} support.
      </div>
    } @else {
      <div class="alert alert-warning m-3" role="alert">
        <mat-icon class="align-middle mr-2">warning</mat-icon>
        This is a partner role. Its name and permissions come from the partner's permission template (Admin &gt; Users &gt; Partner) and are replaced by the next partner sync. Change the template instead.
      </div>
    }
  }

  <div class="p-3" [class.rbac-readonly]="isReadOnly">
    <!-- Header Section with Role Name and Summary -->
    <div class="rbac-header">
      <div class="role-name-section">
        <mat-form-field class="role-name-field" appearance="outline" subscriptSizing="dynamic">
          <mat-label>Role Name</mat-label>
          <input
            matInput
            placeholder="Enter role name"
            [(ngModel)]="rbac_role.name"
            name="name"
            type="text"
            id="name"
            [disabled]="isReadOnly"
            required />
        </mat-form-field>
      </div>

      <div class="permission-summary">
        <div class="summary-stat">
          <span class="stat-value">{{ getEnabledPermissionsCount() }}</span>
          <span class="stat-label">of {{ getTotalPermissionsCount() }} permissions</span>
        </div>
        <div class="summary-progress">
          <mat-progress-bar
            mode="determinate"
            [value]="getEnabledPercentage()"
            [color]="getEnabledPercentage() > 50 ? 'primary' : 'accent'">
          </mat-progress-bar>
          <span class="progress-label">{{ getEnabledPercentage() }}% configured</span>
        </div>
      </div>
    </div>

    <!-- Global Quick Actions -->
    <div class="global-quick-actions">
      <span class="quick-actions-label">Set all permissions:</span>
      <div class="quick-actions-buttons">
        <button
          mat-stroked-button
          type="button"
          class="global-action-btn full-access"
          (click)="setAllFullAccess()"
          matTooltip="Grant full access to all features">
          <mat-icon>done_all</mat-icon>
          Full Access
        </button>
        <button
          mat-stroked-button
          type="button"
          class="global-action-btn view-only"
          (click)="setAllViewOnly()"
          matTooltip="Grant view-only access to all features">
          <mat-icon>visibility</mat-icon>
          View Only
        </button>
        <button
          mat-stroked-button
          type="button"
          class="global-action-btn no-access"
          (click)="setAllNoAccess()"
          matTooltip="Remove all access">
          <mat-icon>block</mat-icon>
          No Access
        </button>
      </div>
    </div>

    <!-- Permission dependency summary: ticked permissions that need a parent permission to work. -->
    <ng-container *ngIf="getAllUnmetDependencies() as unmetDependencies">
      <div class="dependency-summary" *ngIf="unmetDependencies.length > 0" role="status">
        <mat-icon class="dependency-summary-icon">warning</mat-icon>
        <span class="dependency-summary-text">
          {{ unmetDependencies.length }} {{ unmetDependencies.length === 1 ? 'feature needs' : 'features need' }} another permission to work in the Web Portal. Look for the amber <b>Needs</b> labels below.
        </span>
        <button mat-stroked-button type="button" class="dependency-fix-all-btn" (click)="onFixAllDependencies()">
          <mat-icon>playlist_add_check</mat-icon>
          Add missing permissions
        </button>
      </div>
    </ng-container>

    <!-- Search and Controls Section -->
    <div class="search-controls-section">
      <mat-form-field class="search-field" appearance="outline" subscriptSizing="dynamic">
        <mat-icon matPrefix>search</mat-icon>
        <mat-label>Search features...</mat-label>
        <input
          matInput
          placeholder="Type to filter features..."
          [(ngModel)]="searchText"
          name="search"
          type="text"
          (ngModelChange)="onFilterFeatures()">
        <button mat-icon-button matSuffix *ngIf="searchText" (click)="clearSearch()" type="button">
          <mat-icon>clear</mat-icon>
        </button>
      </mat-form-field>

      <div class="control-buttons">
        <button mat-button type="button" (click)="toggleAllCategories()" [matTooltip]="areAllCategoriesExpanded() ? 'Collapse all categories' : 'Expand all categories'">
          <mat-icon>{{ areAllCategoriesExpanded() ? 'unfold_less' : 'unfold_more' }}</mat-icon>
          {{ areAllCategoriesExpanded() ? 'Collapse All' : 'Expand All' }}
        </button>
      </div>
    </div>

    <!-- Categories Accordion -->
    <div class="categories-container" *ngIf="getSortedCategoryKeys().length > 0; else noResults">
      <mat-accordion multi>
        <mat-expansion-panel
          *ngFor="let categoryKey of getSortedCategoryKeys()"
          [expanded]="filtered_categories[categoryKey].expanded"
          (opened)="filtered_categories[categoryKey].expanded = true"
          (closed)="filtered_categories[categoryKey].expanded = false"
          class="category-panel">

          <!-- Category Header -->
          <mat-expansion-panel-header>
            <mat-panel-title>
              <div class="category-header-content">
                <mat-icon class="category-icon">{{ filtered_categories[categoryKey].icon }}</mat-icon>
                <span class="category-name">{{ categoryKey }}</span>
                <span class="category-badge" [class.has-permissions]="getCategoryPermissionCount(categoryKey).enabled > 0">
                {{ getCategoryPermissionCount(categoryKey).enabled }}/{{ getCategoryPermissionCount(categoryKey).total }}
              </span>
                <span
                  class="category-dependency-badge"
                  *ngIf="getCategoryUnmetCount(categoryKey) as unmetCount"
                  [matTooltip]="unmetCount + (unmetCount === 1 ? ' feature in this category needs' : ' features in this category need') + ' another permission to work.'">
                  <mat-icon>warning</mat-icon>{{ unmetCount }}
                </span>
              </div>
            </mat-panel-title>
            <mat-panel-description class="category-quick-actions" (click)="$event.stopPropagation()">
              <button
                mat-stroked-button
                type="button"
                class="quick-action-btn full-access"
                (click)="setCategoryFullAccess(categoryKey)"
                matTooltip="Grant full access to all features in this category">
                Full Access
              </button>
              <button
                mat-stroked-button
                type="button"
                class="quick-action-btn view-only"
                (click)="setCategoryViewOnly(categoryKey)"
                matTooltip="Grant view-only access to all features in this category">
                View Only
              </button>
              <button
                mat-stroked-button
                type="button"
                class="quick-action-btn no-access mr-3"
                (click)="setCategoryNoAccess(categoryKey)"
                matTooltip="Remove all access from this category">
                No Access
              </button>
            </mat-panel-description>
          </mat-expansion-panel-header>

          <!-- Dependency hints for this category, e.g. "Site features also need Sites: View". -->
          <ng-container *ngIf="getCategoryDependencyHints(categoryKey) as hints">
            <div class="category-dependency-hints" *ngIf="hints.length > 0">
              <div class="category-dependency-hint" *ngFor="let hint of hints">
                <mat-icon>info</mat-icon>
                <span>{{ hint }}</span>
              </div>
            </div>
          </ng-container>

          <!-- Features Table -->
          <div class="features-container">
            <table class="features-table">
              <thead>
              <tr>
                <th class="feature-name-col">Feature</th>
                <th class="permission-col" *ngFor="let action of availableActions">
                  <mat-icon>{{ getIconForAction(action) }}</mat-icon>
                  <span>{{ action | titlecase }}</span>
                </th>
                <th class="quick-actions-col">Quick Actions</th>
              </tr>
              </thead>
              <tbody>
              <tr *ngFor="let feature of filtered_categories[categoryKey].features | keyvalue" class="feature-row">
                <td class="feature-name-cell">
                  <span class="feature-name">{{ feature.key }}</span>
                  <ng-container *ngIf="getFeatureDescription(feature.value.rbac_permissions) as featureDescription">
                    <button
                      type="button"
                      class="rbac-feature-info-btn"
                      [attr.aria-label]="'What does ' + feature.key + ' cover?'"
                      [matTooltip]="featureDescription"
                      matTooltipPosition="above"
                      matTooltipClass="rbac-feature-tooltip"
                      [matTooltipShowDelay]="300"
                      (click)="$event.stopPropagation()">
                      <mat-icon aria-hidden="true">info_outline</mat-icon>
                    </button>
                  </ng-container>
                  <span class="feature-count" [class.all-enabled]="hasFeatureFullAccess(feature.value.rbac_permissions)">
                    {{ getFeaturePermissionCount(feature.value.rbac_permissions).enabled }}/{{ getFeaturePermissionCount(feature.value.rbac_permissions).total }}
                  </span>
                  <button
                    *ngFor="let unmet of getUnmetDependencies(feature.value.rbac_permissions); trackBy: trackUnmetDependency"
                    type="button"
                    class="dependency-chip"
                    (click)="onFixDependency(unmet)"
                    [matTooltip]="getDependencyTooltip(unmet)"
                    [attr.aria-label]="'Needs ' + unmet.label + '. Select to add it.'">
                    <mat-icon>warning</mat-icon>
                    Needs {{ unmet.label }}
                    <span class="dependency-chip-action" aria-hidden="true"><mat-icon>add</mat-icon>Add</span>
                  </button>
                </td>
                <td class="permission-cell" *ngFor="let action of availableActions">
                  <ng-container *ngIf="getPermissionForAction(feature.value.rbac_permissions, action) as permission; else noPermission">
                    <mat-checkbox
                      [(ngModel)]="permission.checked"
                      [name]="'rbac_permission_' + permission.id"
                      (change)="onPermissionChange(permission, feature.value.rbac_permissions)">
                    </mat-checkbox>
                  </ng-container>
                  <ng-template #noPermission>
                    <span class="no-permission">-</span>
                  </ng-template>
                </td>
                <td class="quick-actions-cell">
                  <div class="feature-quick-actions">
                    <button
                      mat-icon-button
                      type="button"
                      class="feature-action-btn"
                      [class.active]="hasFeatureFullAccess(feature.value.rbac_permissions)"
                      (click)="setFeatureFullAccess(feature.value.rbac_permissions)"
                      matTooltip="Full Access">
                      <mat-icon>done_all</mat-icon>
                    </button>
                    <button
                      mat-icon-button
                      type="button"
                      class="feature-action-btn"
                      [class.active]="hasFeatureViewOnly(feature.value.rbac_permissions)"
                      (click)="setFeatureViewOnly(feature.value.rbac_permissions)"
                      matTooltip="View Only">
                      <mat-icon>visibility</mat-icon>
                    </button>
                    <button
                      mat-icon-button
                      type="button"
                      class="feature-action-btn"
                      [class.active]="hasFeatureNoAccess(feature.value.rbac_permissions)"
                      (click)="setFeatureNoAccess(feature.value.rbac_permissions)"
                      matTooltip="No Access">
                      <mat-icon>block</mat-icon>
                    </button>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </mat-expansion-panel>
      </mat-accordion>
    </div>

    <!-- No Results Message -->
    <ng-template #noResults>
      <div class="no-results-container">
        <mat-icon class="no-results-icon">search_off</mat-icon>
        <p class="no-results-text">No features found matching "{{ searchText }}"</p>
        <button mat-stroked-button type="button" (click)="clearSearch()">Clear Search</button>
      </div>
    </ng-template>
  </div>
</form>
